<template>
  <div>
    <b-tabs v-model="activeSubTabIndex" :nav-class="['mainNav', { 'hide-titles': !hasKiids || !hasSids }]" class="fairmat-primary-tabs px-3">
      <b-tab v-if="hasSids" lazy>
        <template v-slot:title v-if="hasKiids"> SID<b-badge class="ml-2">{{ sids.length }}</b-badge> </template>
        <sids-table
          class="mt-4"
          :value="sortedSids"
          :reference="sortedSids"
          :selected-sid.sync="selectedSidIndex"
          :order-by="sidsOrderBy"
          :order-asc="sidsOrderAsc"
          @setSort="setSidsSort"
        ></sids-table>
      </b-tab>
      <b-tab v-if="hasKiids" lazy>
        <template v-slot:title> KIID<b-badge class="ml-2">{{ kiids.length }}</b-badge> </template>
        <kiids-list-table :items="kiids" :selected-id="selectedOptionId" @select="selectKiid"></kiids-list-table>
      </b-tab>
    </b-tabs>
    <investment-option-detail-panel class="px-3"></investment-option-detail-panel>
  </div>
</template>

<script>
// "Opzioni di investimento — lista" (InvestmentOptionsList), IBIPs-only, its own top-level
// InvestmentOptions tab (distinct from InvestmentOptionsProfile, which stays inside KpiKid).
// MOP-only, per the backend's InvestmentOptionsListPayload doc comment.
//
// SID sub-tab reuses components/ProductDetails/SidsTable.vue (prop-driven, no Vuex/parent
// coupling) — a MOP product can have thousands of investment options, so the table's
// DOM-virtualization is needed. `InvestmentOptionSidDto.{id,name,sri,rhp}` already
// matches SidsTable.vue's own `{id,name,sri,rhp}` reads field-for-field — only `.index` is
// appended (SidsTable.vue's own row-selection/scroll-to bookkeeping).
//
// KIID sub-tab uses KiidsListTable.vue (own file, see its comment); the per-KIID detail panel is
// InvestmentOptionDetailPanel.vue, shared between both sub-tabs.
//
// `class="px-3"` on <investment-option-detail-panel> (merged onto its own root element, same Vue 2
// non-prop-attribute inheritance already relied on for `<sids-table class="mt-4">` above) provides
// the horizontal padding the sub-tabs get by inheriting it from
// `<b-tabs class="fairmat-primary-tabs px-3">`. This panel is a SIBLING of the SID/KIID b-tabs
// (shared between both sub-tabs — see the mechanism note below), so it does not inherit that
// padding and must restate it explicitly.
//
// In-page drilldown: a row click in either sub-tab calls
// ProductDetails.vue's own selectInvestmentOption() (via the detailPage inject —
// InvestmentOptionDetailPanel.vue's own comment covers the full mechanism/response shape),
// which InvestmentOptionDetailPanel.vue (mounted once below both tabs, shared) then renders. SID
// selection goes through SidsTable.vue's own `.sync` selectedSid (an index into sortedSids, its
// existing row-selection bookkeeping — nothing new added there) rather than a new prop/event, since
// that component already tracks exactly this; KIID selection uses a plain @select event
// (KiidsListTable.vue has no such existing mechanism to reuse).
import _ from 'lodash'
import { base64UrlDecode } from '../../../utils/sanitizer'
import SidsTable from '../../ProductDetails/SidsTable'
import KiidsListTable from './KiidsListTable'
import InvestmentOptionDetailPanel from '../InvestmentOptionDetailPanel'

export default {
  name: 'investment-options-list-section',
  components: {
    SidsTable,
    KiidsListTable,
    InvestmentOptionDetailPanel,
  },
  inject: ['detailPage'],
  props: {
    payload: { type: Object, required: true },
  },
  data() {
    return {
      sidsOrderBy: '',
      sidsOrderAsc: true,
      selectedSidIndex: null,
      activeSubTabIndex: 0,
    }
  },
  computed: {
    sids() {
      return this.payload.sids || []
    },
    kiids() {
      return this.payload.kiids || []
    },
    hasSids() {
      return this.sids.length > 0
    },
    hasKiids() {
      return this.kiids.length > 0
    },
    // Sorts the SIDs and adds the `.index` bookkeeping needed by SidsTable.vue's
    // row-selection/scroll-to-selected logic.
    sortedSids() {
      let sorted = this.sids
      if (this.sidsOrderBy !== '') {
        sorted = _.sortBy(sorted, [
          (sid) => (typeof sid[this.sidsOrderBy] === 'string' ? sid[this.sidsOrderBy].toLowerCase() : sid[this.sidsOrderBy]),
        ])
        if (!this.sidsOrderAsc) {
          sorted = _.reverse(sorted)
        }
      }
      return sorted.map((sid, index) => ({ ...sid, index }))
    },
    // Drives KiidsListTable's own row-highlight — reused verbatim for either sub-tab since a Sid's
    // `id` is a single autoincrement PK across SID and KIID rows alike, no cross-type collision.
    selectedOptionId() {
      return this.detailPage.selectedInvestmentOption ? this.detailPage.selectedInvestmentOption.id : null
    },
  },
  watch: {
    // SidsTable.vue only ever gives back the selected row's INDEX (its own `.sync` contract) — look
    // the actual Sid back up in sortedSids to get the {id, name} selectInvestmentOption() needs.
    selectedSidIndex(index) {
      const sid = this.sortedSids[index]
      if (sid) {
        this.detailPage.selectInvestmentOption(sid.id, sid.name)
      }
    },
  },
  methods: {
    setSidsSort({ orderBy, orderAsc }) {
      this.sidsOrderBy = orderBy
      this.sidsOrderAsc = orderAsc
    },
    selectKiid(item) {
      this.detailPage.selectInvestmentOption(item.id, item.name)
    },
  },
  // Deep-link support: if the URL already names an option (ProductDetails.vue's
  // investmentOptionName prop, still base64url-encoded — decoded here the same way every other
  // investmentOptionName consumer in this app does), select and open it directly, switching to the
  // KIID sub-tab first if that's where it lives. Falls back to selecting the first
  // option (SID preferred over KIID, matching the sub-tab order above) when there's no match. This component is only
  // ever mounted once its own outer b-tab (ProductDetails.vue) is first activated (`lazy`), so
  // `mounted` here already fires exactly on that "tab shown" moment — no separate tab-activate
  // listener needed, and ProductDetails.vue's own loadData() is what makes sure this tab IS
  // activated on load when a deep link is present in the first place.
  mounted() {
    const targetName = this.detailPage.investmentOptionName ? base64UrlDecode(this.detailPage.investmentOptionName) : null
    if (targetName) {
      const sid = this.sortedSids.find((s) => s.name === targetName)
      if (sid) {
        this.selectedSidIndex = sid.index
        return
      }
      const kiid = this.kiids.find((k) => k.name === targetName)
      if (kiid) {
        if (this.hasSids) {
          this.activeSubTabIndex = 1
        }
        this.selectKiid(kiid)
        return
      }
    }
    if (this.hasSids) {
      this.selectedSidIndex = 0
    } else if (this.hasKiids) {
      this.selectKiid(this.kiids[0])
    }
  },
}
</script>

<style scoped>
/* :nav-class binds 'hide-titles' when there's only one real sub-tab (a pure-Sid or pure-Kiid
   option list) — nothing to choose between, so hide the whole nav bar instead of showing a
   single-item tab selector. /deep/, same reasoning as ProductDetails.vue's own .fairmat-secondary-tabs
   rule: nav-class is rendered by b-tabs' own template, not this component's, so it never carries
   this component's scope attribute on its own — reached instead via the scoped ancestor
   (.fairmat-primary-tabs, on the <b-tabs> tag this template does render). */
.fairmat-primary-tabs /deep/ .mainNav.hide-titles {
  display: none;
}
</style>
